import React, { useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { loginUser } from "../../../Store/userSlice";
import logo from "../../../assets/images/logo.png";
import "./Login.css";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailNotVerified, setEmailNotVerified] = useState(false);

  const [searchParams] = useSearchParams();
  const justVerified = searchParams.get("verified") === "true";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailNotVerified(false);

    if (!email.trim()) {
      setError("Veuillez saisir votre adresse e-mail.");
      return;
    }
    if (!password) {
      setError("Veuillez saisir votre mot de passe.");
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(loginUser({ email, password }));
      if (result.payload?.status) {
        const msg = result.payload?.data?.message;
        if (msg === "EMAIL_NOT_VERIFIED") {
          setEmailNotVerified(true);
        } else if (Array.isArray(msg)) {
          setError(msg[0]);
        } else {
          setError(msg || "Identifiants incorrects. Veuillez réessayer.");
        }
      } else {
        navigate("/");
      }
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Logo — click to go home */}
        <NavLink to="/">
          <img src={logo} alt="Casa del Padel" className="login-logo" />
        </NavLink>

        <h1 className="login-title">Bon retour !</h1>
        <p className="login-subtitle">Connectez-vous à votre compte</p>

        {/* Verified success banner */}
        {justVerified && (
          <div className="login-verified-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Adresse e-mail vérifiée ! Vous pouvez maintenant vous connecter.
          </div>
        )}

        {/* Inline error */}
        {error && <div className="login-error">{error}</div>}

        {/* Email not verified banner */}
        {emailNotVerified && (
          <div className="login-unverified-banner">
            <div className="login-unverified-text">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Votre adresse e-mail n'a pas encore été vérifiée.
            </div>
            <NavLink
              to={`/verify-email-sent?email=${encodeURIComponent(email)}`}
              className="login-unverified-link"
            >
              Renvoyer le lien de vérification →
            </NavLink>
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit} noValidate>

          {/* Email */}
          <div className="login-field">
            <label className="login-label" htmlFor="login-email">
              Adresse e-mail
            </label>
            <input
              id="login-email"
              type="email"
              className="login-input"
              placeholder="exemple@mail.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); setEmailNotVerified(false); }}
              autoComplete="email"
              autoCapitalize="none"
            />
          </div>

          {/* Password */}
          <div className="login-field">
            <div className="login-label-row">
              <label className="login-label" htmlFor="login-password">
                Mot de passe
              </label>
              <NavLink to="/forgot-pass" className="login-forgot">
                Mot de passe oublié ?
              </NavLink>
            </div>
            <div className="login-input-wrap">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                className="login-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); setEmailNotVerified(false); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Masquer" : "Afficher"}
              >
                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? "Connexion en cours…" : "Se connecter"}
          </button>
        </form>

        {/* Register link */}
        <p className="login-register-hint">
          Pas encore de compte ?{" "}
          <NavLink to="/register" className="login-register-link">
            S'inscrire
          </NavLink>
        </p>

      </div>
    </div>
  );
};

export default Login;
